import Phaser from 'phaser';
import type { InputFrame, MoveAxis } from '../player/stateMachine';
import { EdgeCounter } from './edgeCounter';

export const TOUCH_TEXTURE = 'touch-button';

interface Button {
  image: Phaser.GameObjects.Image;
  label: Phaser.GameObjects.Text;
  pressed: boolean;
  pointerId: number | null;
}

/**
 * On-screen touch pad: ◀ ▶ on the left, » (run toggle) and ▲ (jump) on the right, II (pause) in
 * the corner. Buttons press on pointer down, follow a sliding thumb (pointer over while down)
 * and release on pointer up anywhere or pointer out. Jump and pause are edges consumed once per
 * frame, like the keyboard; movement and run are held states. Merged with the keyboard through
 * `mergeInputs`, so both can be used at once.
 */
export class TouchPad {
  private readonly buttons: Button[] = [];
  private left!: Button;
  private right!: Button;
  private jump!: Button;
  private run!: Button;
  private pause!: Button;
  private runOn = false;
  private readonly jumpEdges = new EdgeCounter();
  private readonly pauseEdges = new EdgeCounter();
  private visible = true;

  constructor(
    private readonly scene: Phaser.Scene,
    width: number,
    height: number,
    depth: number,
  ) {
    scene.input.addPointer(2);
    ensureTouchTexture(scene);
    const pad = 90;
    this.left = this.button(pad, height - pad, '◀', depth);
    this.right = this.button(pad + 140, height - pad, '▶', depth);
    this.jump = this.button(width - pad, height - pad, '▲', depth);
    this.run = this.button(width - pad - 140, height - pad, '»', depth);
    this.pause = this.button(width - 60, 70, 'II', depth, 0.55);
    scene.input.on(Phaser.Input.Events.POINTER_UP, (pointer: Phaser.Input.Pointer) => {
      for (const b of this.buttons) if (b.pointerId === pointer.id) this.release(b);
    });
  }

  private button(x: number, y: number, glyph: string, depth: number, scale = 1): Button {
    const image = this.scene.add
      .image(x, y, TOUCH_TEXTURE)
      .setScale(scale)
      .setScrollFactor(0)
      .setDepth(depth)
      .setAlpha(0.75)
      .setInteractive();
    const label = this.scene.add
      .text(x, y, glyph, {
        fontFamily: "'Trebuchet MS', 'Segoe UI', system-ui, sans-serif",
        fontSize: `${Math.round(44 * scale)}px`,
        fontStyle: 'bold',
        color: '#ffffff',
        stroke: '#1d2a36',
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(depth + 1);
    const b: Button = { image, label, pressed: false, pointerId: null };
    image.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, (pointer: Phaser.Input.Pointer) =>
      this.press(b, pointer),
    );
    image.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OVER, (pointer: Phaser.Input.Pointer) => {
      if (pointer.isDown) this.press(b, pointer);
    });
    image.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OUT, () => this.release(b));
    image.on(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, () => this.release(b));
    this.buttons.push(b);
    return b;
  }

  private press(b: Button, pointer: Phaser.Input.Pointer): void {
    if (b.pressed) return;
    b.pressed = true;
    b.pointerId = pointer.id;
    b.image.setAlpha(1).setScale(b.image.scale * 1.08);
    if (b === this.jump) this.jumpEdges.record({ pointer: pointer.id, t: pointer.downTime });
    if (b === this.pause) this.pauseEdges.record({ pointer: pointer.id, t: pointer.downTime });
    if (b === this.run) {
      this.runOn = !this.runOn;
      this.run.label.setColor(this.runOn ? '#ffd23f' : '#ffffff');
    }
  }

  private release(b: Button): void {
    if (!b.pressed) return;
    b.pressed = false;
    b.pointerId = null;
    b.image.setAlpha(0.75).setScale(b.image.scale / 1.08);
  }

  /** This frame's held movement/run plus the jump edge (consumed). Call once per frame. */
  snapshot(): InputFrame {
    const left = this.left.pressed;
    const right = this.right.pressed;
    const moveAxis: MoveAxis = left === right ? 0 : left ? -1 : 1;
    return { moveAxis, runHeld: this.runOn, jumpPressed: this.jumpEdges.consume() > 0 };
  }

  consumePause(): boolean {
    return this.pauseEdges.consume() > 0;
  }

  get runToggled(): boolean {
    return this.runOn;
  }

  setVisible(on: boolean): void {
    this.visible = on;
    for (const b of this.buttons) {
      b.image.setVisible(on);
      b.label.setVisible(on);
      if (!on) this.release(b);
    }
  }

  get isVisible(): boolean {
    return this.visible;
  }

  releaseAll(): void {
    for (const b of this.buttons) this.release(b);
    this.jumpEdges.reset();
    this.pauseEdges.reset();
  }
}

function ensureTouchTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(TOUCH_TEXTURE)) return;
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  g.fillStyle(0xffffff, 0.22);
  g.fillCircle(55, 55, 52);
  g.lineStyle(4, 0xffffff, 0.55);
  g.strokeCircle(55, 55, 52);
  g.generateTexture(TOUCH_TEXTURE, 110, 110);
  g.destroy();
}
